import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Copy, Bell } from "lucide-react";
import { api } from "@/lib/api";
import { LoadingBlock, ErrorState, toast, Button } from "@/components/ds";
import { PageTitle, Stat, Badge, Card, money, pct } from "./_ui";

export default function MyPerformance() {
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["sales-me"], queryFn: () => api.salesMe() });
  const notes = useQuery({ queryKey: ["sales-me-notes"], queryFn: () => api.salesMyNotifications() });
  const activity = useQuery({ queryKey: ["sales-me-activity"], queryFn: () => api.salesMyActivity() });
  if (isLoading) return <LoadingBlock rows={6} />;
  if (isError || !data) return <ErrorState onRetry={refetch} />;
  const s = data.summary;

  const copyLink = async (plan: string) => {
    try { const r = await api.salesCheckoutLink(plan); await navigator.clipboard.writeText(r.url); toast.success(`${plan} checkout link copied`); }
    catch { toast.error("Could not generate link"); }
  };

  const Progress = ({ label, current, next, tone }: any) => (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`prog-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <p className={`mt-1 font-display text-xl font-semibold ${tone}`}>{current}</p>
      {next ? <p className="mt-1 text-xs text-slate-500">{next.away} more → +{money(next.next_amount)} <span className="text-slate-400">(at {next.next_threshold})</span></p>
            : <p className="mt-1 text-xs text-emerald-600">Top tier reached</p>}
    </div>
  );

  return (
    <div data-testid="my-performance">
      <PageTitle title="My Performance" subtitle={`Period ${String(s.period_start).slice(0,10)} → ${String(s.period_end).slice(0,10)} · ${s.days_remaining} days remaining`} right={<Badge status={s.quota_status} />} />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Quota" value={`${s.total_paid_sales} / ${s.quota}`} tone={s.total_paid_sales >= s.quota ? "emerald" : "slate"} testid="mp-quota" />
        <Stat label="Growth Mix" value={pct(s.growth_mix_percent)} />
        <Stat label="Earned (Total Owed)" value={money(s.total_owed)} tone="indigo" testid="mp-owed" />
        <Stat label="Outstanding" value={money(s.outstanding)} tone="amber" />
      </div>

      <div className="mt-6" data-testid="sales-activity">
        <p className="mb-2 text-sm font-semibold text-slate-700">My Sales Activity</p>
        <div className="grid gap-4 lg:grid-cols-3">
          {(activity.data?.windows || []).map((w: any) => (
            <div key={w.key} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`activity-${w.key}`}>
              <div className="flex items-baseline justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{w.label}</p>
                <p className="font-display text-2xl font-semibold text-slate-900" data-testid={`activity-${w.key}-total`}>{w.total}</p>
              </div>
              <p className="mt-0.5 text-xs text-slate-400">sales made · {money(w.commission)} commission</p>
              <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                <ActRow label="Starter" v={w.by_plan.starter} tone="text-slate-700" />
                <ActRow label="Growth" v={w.by_plan.growth} tone="text-emerald-600" />
                <ActRow label="Pro" v={w.by_plan.pro} tone="text-blue-600" />
                <ActRow label="Custom" v={w.by_plan.custom} tone="text-indigo-600" />
              </div>
              <div className="mt-3 flex gap-2 border-t border-slate-100 pt-3 text-xs">
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600" data-testid={`activity-${w.key}-standard`}>Standard: {w.standard}</span>
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-700" data-testid={`activity-${w.key}-promo`}>Promo: {w.promo}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <p className="mt-5 text-xs font-semibold uppercase tracking-widest text-slate-400">Monthly sales · {s.monthly_sales ?? 0} this period</p>
      <div className="mt-2 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Progress label="Next General Bonus" current={`${s.monthly_sales ?? 0} sales`} next={data.next_general_bonus} tone="text-slate-900" />
        <Progress label="Growth Accelerator" current={`${s.monthly_by_plan?.growth ?? 0} growth`} next={data.next_growth_tier} tone="text-emerald-600" />
        <Progress label="Pro Accelerator" current={`${s.monthly_by_plan?.pro ?? 0} pro`} next={data.next_pro_tier} tone="text-blue-600" />
        <Progress label="Custom Accelerator" current={`${s.monthly_by_plan?.custom ?? 0} custom`} next={data.next_custom_tier} tone="text-indigo-600" />
      </div>

      {/* Annual sales are their own ladders. Reaching a tier on both earns both. */}
      <p className="mt-5 text-xs font-semibold uppercase tracking-widest text-slate-400">Annual sales · {s.annual_sales ?? 0} this period</p>
      <div className="mt-2 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Progress label="Next Annual Bonus" current={`${s.annual_sales ?? 0} annual`} next={data.next_annual_bonus} tone="text-slate-900" />
        <Progress label="Growth Annual Accelerator" current={`${s.annual_by_plan?.growth ?? 0} growth`} next={data.next_annual_growth_tier} tone="text-emerald-600" />
        <Progress label="Pro Annual Accelerator" current={`${s.annual_by_plan?.pro ?? 0} pro`} next={data.next_annual_pro_tier} tone="text-blue-600" />
        <Progress label="Custom Annual Accelerator" current={`${s.annual_by_plan?.custom ?? 0} custom`} next={data.next_annual_custom_tier} tone="text-indigo-600" />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Card title="My Compensation (earned)">
          <dl className="space-y-2 text-sm">
            <Row k="Base Commission" v={money(s.base_commission)} />
            <Row k="General Performance Bonus" v={money(s.general_performance_bonus)} />
            <Row k="Growth Accelerator" v={money(s.growth_accelerator)} />
            <Row k="Pro Accelerator" v={money(s.pro_accelerator)} />
            <Row k="Custom Accelerator" v={money(s.custom_accelerator)} />
            {(s.annual_bonuses ?? 0) > 0 && (
              <>
                <div className="my-1 border-t border-slate-100" />
                <Row k="Annual Sales Performance" v={money(s.annual_performance_bonus)} />
                <Row k="Growth Annual Accelerator" v={money(s.annual_growth_accelerator)} />
                <Row k="Pro Annual Accelerator" v={money(s.annual_pro_accelerator)} />
                <Row k="Custom Annual Accelerator" v={money(s.annual_custom_accelerator)} />
              </>
            )}
            <Row k="Adjustments" v={money(s.adjustments)} />
            <div className="my-1 border-t border-slate-100" />
            <Row k="Total Owed" v={money(s.total_owed)} bold />
            <Row k="Total Paid" v={money(s.total_paid)} />
          </dl>
          {(s.held_commission ?? 0) > 0 && (
            <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="my-held-commission">
              {money(s.held_commission)} from annual sales is held until the 30-day refund window closes, then it becomes payable.
            </p>
          )}
          {(s.review_commission ?? 0) > 0 && (
            <p className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="my-review-commission">
              {money(s.review_commission)} is on hold pending a review of a partial refund. It is not on a timer — someone will decide it.
            </p>
          )}
          <p className="mt-3 text-xs text-slate-400">Earned = payment cleared &amp; tier achieved. Amounts shown as "away" are In Progress, not guaranteed.</p>
        </Card>

        <Card title="My Checkout Links" right={<span className="text-xs text-slate-400">Attributes sales to you</span>}>
          <div className="space-y-2">
            {["growth", "starter", "pro"].map((p) => (
              <button key={p} onClick={() => copyLink(p)} className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50" data-testid={`copy-link-${p}`}>
                <span className="capitalize">{p} checkout link</span><Copy className="h-4 w-4 text-slate-400" />
              </button>
            ))}
          </div>
          <PayoutSettings />
        </Card>
      </div>

      <div className="mt-6">
        <Card title="Notifications">
          {(notes.data?.notifications || []).length === 0 ? <p className="py-4 text-center text-sm text-slate-400">No notifications yet.</p> : (
            <ul className="space-y-2 text-sm" data-testid="mp-notifications">
              {notes.data!.notifications.map((n: any) => (
                <li key={n.id} className="flex items-start gap-2"><Bell className="mt-0.5 h-4 w-4 text-blue-400" /><span>{n.message} <span className="text-xs text-slate-400">· {String(n.created_at).slice(0,16)}</span></span></li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

function Row({ k, v, bold }: { k: string; v: string; bold?: boolean }) {
  return <div className="flex justify-between"><dt className="text-slate-500">{k}</dt><dd className={`${bold ? "font-semibold" : "font-medium"} text-slate-900`}>{v}</dd></div>;
}

function ActRow({ label, v, tone }: { label: string; v: number; tone: string }) {
  return (
    <div className="flex items-center justify-between rounded-md bg-slate-50 px-2 py-1">
      <span className="text-xs text-slate-500">{label}</span>
      <span className={`font-semibold ${tone}`}>{v}</span>
    </div>
  );
}

function PayoutSettings() {
  const { data, refetch } = useQuery({ queryKey: ["connect-status"], queryFn: () => api.salesConnectStatus() });
  const onboard = async () => {
    try { const r = await api.salesConnectOnboard(); window.location.href = r.url; }
    catch { toast.error("Stripe Payout Integration Required — ask your admin to enable Connect."); }
  };
  return (
    <div className="mt-4 border-t border-slate-100 pt-4" data-testid="payout-settings">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Payout Settings</p>
      {!data ? <p className="text-xs text-slate-400">Loading…</p> : !data.connect_configured ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="payout-not-configured">
          Stripe Payout Integration Required. Contact your ResFil administrator to enable payouts.
        </div>
      ) : (
        <div className="space-y-2 text-sm">
          <Badge status={data.payout_ready ? "paid" : "pending"} />
          <p className="text-xs text-slate-500">Status: {data.connect_status}</p>
          {!data.payout_ready && <Button size="sm" onClick={onboard} data-testid="setup-payouts">Set Up Payouts</Button>}
        </div>
      )}
    </div>
  );
}
